import { ArrowRight, FolderPlus, Layers, List, Plus, Search, Shield } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column, type Sort } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { LoadingRows, SearchInput } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { useCachedList } from '@/lib/useCachedList'
import { useSelection } from '@/lib/useSelection'

type ProjectExtra = {
  type?: string | null
  lead?: { account_id: string | null; display_name: string | null } | null
  category?: string | null
  archived?: boolean | null
}
type Project = { key: string; name: string; id: string; extra?: ProjectExtra | null }

/** Shortcuts to project operations — they open the operation page. */
const TOOLS = [
  { label: 'Create project', icon: Plus, op: 'projects.create_project' },
  { label: 'Find projects', icon: Search, op: 'projects.find_projects' },
  { label: 'Project types', icon: List, op: 'projects.list_accessible_project_types' },
  { label: 'Categories', icon: FolderPlus, op: 'projects.list_project_categories' },
  { label: 'Permission schemes', icon: Shield, op: 'projects.list_permission_schemes' },
  { label: 'Notification schemes', icon: Layers, op: 'projects.list_notification_schemes' },
  { label: 'Issue-type schemes', icon: Layers, op: 'projects.list_issue_type_schemes' },
]

function sortProjects(projects: Project[], sort: Sort): Project[] {
  const dir = sort.dir === 'asc' ? 1 : -1
  const key = sort.key === 'name' ? 'name' : 'key'
  return [...projects].sort((a, b) => a[key].localeCompare(b[key], undefined, { sensitivity: 'base' }) * dir)
}

const lead = (p: Project) => p.extra?.lead?.display_name ?? '—'

const COLUMNS: Column<Project>[] = [
  { key: 'key', header: 'Key', mono: true, width: '110px', sortable: true },
  {
    key: 'name', header: 'Name', sortable: true, title: (p) => p.name,
    render: (p) => (
      <>
        {p.name}
        {p.extra?.archived && (
          <span className="ml-2 rounded border border-line-strong px-1 text-[11px] text-text-2">archived</span>
        )}
      </>
    ),
  },
  { key: 'type', header: 'Type', mono: true, width: '14%', render: (p) => p.extra?.type ?? '—' },
  { key: 'lead', header: 'Lead', width: '18%', render: lead, title: lead },
  { key: 'category', header: 'Category', width: '16%', render: (p) => p.extra?.category ?? '—' },
]

function ProjectSummary({ project: p }: { project: Project }) {
  return (
    <DetailPane
      eyebrow="Project"
      title={<><span className="font-mono">{p.key}</span> <span className="font-normal text-text-2">{p.name}</span></>}
      status={p.extra?.archived ? <span className="text-text-2">archived</span> : undefined}
      actions={(
        <Link to={`/projects/${encodeURIComponent(p.key)}`} state={{ project: p }} className={detailActionClass}>
          Open project <ArrowRight aria-hidden className="size-3.5" />
        </Link>
      )}
    >
      <KeyValue
        items={[
          ['Type', <span className="font-mono">{p.extra?.type ?? '—'}</span>],
          ['Lead', lead(p)],
          ['Category', p.extra?.category ?? '—'],
          ['Project ID', <span className="font-mono text-[12px] text-text-2">{p.id}</span>],
        ]}
      />
    </DetailPane>
  )
}

export default function ProjectsList() {
  const [q, setQ] = useState('')
  const projects = useCachedList<Project>('/projects', q)
  const [sort, setSort] = useState<Sort>({ key: 'key', dir: 'asc' })
  const [sel, setSel] = useSelection()

  const rows = useMemo(() => (Array.isArray(projects) ? sortProjects(projects, sort) : []), [projects, sort])
  const selected = rows.find((p) => p.key === sel) ?? null
  const onSort = (key: string) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  return (
    <Page
      title="Projects"
      hint="Browse projects; manage versions, components and access."
      context={<SearchInput value={q} onChange={setQ} placeholder="Search key or name" label="Search projects" />}
      freshness={Array.isArray(projects) ? <span className="font-mono">{projects.length} projects</span> : undefined}
    >
      <FilterBar>
        <span className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">Tools</span>
        {TOOLS.map(({ label, icon: Icon, op }) => (
          <Link key={op} to={`/operations/${op}`} className={detailActionClass}>
            <Icon aria-hidden className="size-3.5" />
            {label}
          </Link>
        ))}
      </FilterBar>
      <SectionBoundary label="Projects" resetKeys={[projects]}>
        <SplitView
          onCloseDetail={() => setSel(null)}
          detail={selected && <ProjectSummary project={selected} />}
          list={projects === 'error'
            ? (
              <p role="alert" className="px-4 py-4 text-[13px] text-danger">
                Couldn’t load projects — the cache answered something unexpected. Sync from Jira in Settings → Jira cache.
              </p>
            )
            : projects === null
              ? <LoadingRows label="Loading projects" />
              : (
                <DataTable
                  label="Projects"
                  rows={rows}
                  columns={COLUMNS}
                  rowId={(p) => p.key}
                  selectedId={sel}
                  onSelect={setSel}
                  sort={sort}
                  onSort={onSort}
                  empty={q ? 'No projects match that query.' : 'No projects — Sync from Jira in Settings → Jira cache.'}
                />
              )}
        />
      </SectionBoundary>
    </Page>
  )
}
